import React, { useState, useEffect } from 'react';
import { leavesAPI, employeesAPI } from '../services/api';
import toast from 'react-hot-toast';
import {
  Settings as SettingsIcon,
  Save,
  Search,
  Plus,
  Edit2,
  X,
  CheckSquare,
  Square,
  Users,
  Calendar,
  Layers
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const YearlyLeaveSettings = () => {
  const [year, setYear] = useState(new Date().getFullYear());
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [leaveAllocations, setLeaveAllocations] = useState({});
  const [loading, setLoading] = useState(false);

  // Matrix View State
  const [assignedBalances, setAssignedBalances] = useState([]);
  const [loadingBalances, setLoadingBalances] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingCell, setEditingCell] = useState(null); // { employeeId, leaveTypeId }
  const [editValue, setEditValue] = useState('');

  // Leave Type Modal State
  const [showLeaveTypeModal, setShowLeaveTypeModal] = useState(false);
  const [selectedLeaveType, setSelectedLeaveType] = useState(null);
  const [leaveTypeFormData, setLeaveTypeFormData] = useState({
    name: '',
    description: '',
    defaultDays: 0,
    requiresApproval: true,
    requiresDocument: false,
    color: '#3B82F6'
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadAssignedBalances = async () => {
    setLoadingBalances(true);
    try {
      const response = await leavesAPI.getAllEmployeeBalances(year);
      setAssignedBalances(response.data);
    } catch (error) {
      toast.error('Failed to load balances');
    } finally {
      setLoadingBalances(false);
    }
  };

  useEffect(() => {
    if (year) {
      loadAssignedBalances();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year]);

  const loadData = async () => {
    try {
      const [typesRes, employeesRes] = await Promise.all([
        leavesAPI.getLeaveTypes(),
        employeesAPI.getAll()
      ]);
      setLeaveTypes(typesRes.data);
      setEmployees(employeesRes.data);

      const allocations = {};
      typesRes.data.forEach(type => {
        allocations[type._id] = type.defaultDays;
      });
      setLeaveAllocations(allocations);
    } catch (error) {
      toast.error('Failed to load initial data');
    }
  };

  const handleSelectAll = () => {
    if (selectedEmployees.length === employees.length) {
      setSelectedEmployees([]);
    } else {
      setSelectedEmployees(employees.map(e => e._id));
    }
  };

  const handleEmployeeToggle = (employeeId) => {
    setSelectedEmployees(prev =>
      prev.includes(employeeId)
        ? prev.filter(id => id !== employeeId)
        : [...prev, employeeId]
    );
  };

  const handleBulkAssign = async () => {
    if (!selectedEmployees.length) {
      toast.error('Please select at least one employee');
      return;
    }

    setLoading(true);
    try {
      await leavesAPI.bulkAssignLeaveBalances({
        employeeIds: selectedEmployees,
        leaveAllocations,
        year
      });
      toast.success(`Allocations updated for ${selectedEmployees.length} employees`);
      setSelectedEmployees([]);
      loadAssignedBalances();
    } catch (error) {
      toast.error('Failed to assign leave balances');
    } finally {
      setLoading(false);
    }
  };

  // Inline Editing Logic
  const handleCellClick = (employeeId, leaveTypeId, currentValue) => {
    setEditingCell({ employeeId, leaveTypeId });
    setEditValue(currentValue);
  };

  const handleCellSave = async (employeeId, balanceId) => {
    if (!balanceId) {
      toast.error("Please assign leaves first");
      setEditingCell(null);
      return;
    }

    try {
      await leavesAPI.updateLeaveBalance(balanceId, editingCell.leaveTypeId, parseInt(editValue));
      toast.success("Updated");
      setEditingCell(null);
      loadAssignedBalances();
    } catch (err) {
      toast.error("Failed to update");
    }
  };

  const getBalanceForEmployee = (employeeId, leaveTypeId) => {
    const empRecord = assignedBalances.find(r => r.employeeId._id === employeeId || r.employeeId === employeeId);
    if (!empRecord || !empRecord.balances) return null;
    const balance = empRecord.balances.find(b => {
      const bTypeId = typeof b.leaveTypeId === 'object' ? b.leaveTypeId._id : b.leaveTypeId;
      return bTypeId === leaveTypeId;
    });
    return balance;
  };

  const handleCreateLeaveType = () => {
    setSelectedLeaveType(null);
    setLeaveTypeFormData({
      name: '',
      description: '',
      defaultDays: 0,
      requiresApproval: true,
      requiresDocument: false,
      color: '#3B82F6'
    });
    setShowLeaveTypeModal(true);
  };

  const handleEditLeaveType = (leaveType) => {
    setSelectedLeaveType(leaveType);
    setLeaveTypeFormData(leaveType);
    setShowLeaveTypeModal(true);
  };

  const handleSaveLeaveType = async () => {
    try {
      if (selectedLeaveType) {
        await leavesAPI.updateLeaveType(selectedLeaveType._id, leaveTypeFormData);
        toast.success('Leave type updated');
      } else {
        await leavesAPI.createLeaveType(leaveTypeFormData);
        toast.success('Leave type created');
      }
      setShowLeaveTypeModal(false);
      loadData();
    } catch (error) {
      toast.error('Failed to save leave type');
    }
  };

  const filteredEmployees = employees.filter(emp =>
    emp.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    emp.employeeId.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="">
      <div className=" space-y-8">

        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-lg font-semibold text-slate-900 flex items-center gap-3">
              <span className="p-2.5 bg-gray-900 rounded-md text-white shadow-none">
                <SettingsIcon size={24} />
              </span>
              Yearly Settings
            </h1>
            <p className="mt-1.5 text-slate-500 font-medium ml-1">Configure annual allocations and quotas</p>
          </div>

          <div className="flex items-center gap-3 bg-white p-1.5 rounded-md border border-slate-200 shadow-sm">
            <button
              onClick={() => setYear(year - 1)}
              className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-gray-700 hover:bg-slate-50 rounded-lg transition-colors"
            >
              ←
            </button>
            <div className="px-4 py-1 font-bold text-slate-700 bg-slate-50 rounded-lg border border-slate-100 min-w-[80px] text-center">
              {year}
            </div>
            <button
              onClick={() => setYear(year + 1)}
              className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-gray-700 hover:bg-slate-50 rounded-lg transition-colors"
            >
              →
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">

          {/* Left Column: Configuration Wizard */}
          <div className="xl:col-span-1 space-y-6">

            {/* 1. Allocation Rules */}
            <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden group">
              <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between bg-gradient-to-r from-slate-50/50 to-white">
                <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                  <Layers size={18} className="text-gray-700" />
                  1. Define Allocations
                </h3>
                <button
                  onClick={handleCreateLeaveType}
                  className="text-xs px-3 py-1.5 bg-white border border-slate-200 text-slate-600 hover:text-gray-700 hover:border-gray-200 rounded-lg font-bold transition-all flex items-center gap-1.5 shadow-sm"
                >
                  <Plus size={12} />
                  New Type
                </button>
              </div>
              <div className="p-4 space-y-3">
                <p className="text-xs text-slate-400 px-2 font-medium uppercase tracking-wide">Leave Types & Defaults ({year})</p>
                {leaveTypes.map(type => (
                  <div key={type._id} className="flex items-center justify-between py-2 px-3 rounded-md hover:bg-slate-50 transition-colors border border-transparent hover:border-slate-100">
                    <div className="flex items-center flex-1">
                      <div className="w-8 h-8 rounded-lg flex items-center justify-center mr-3 font-bold text-xs shadow-sm text-white" style={{ backgroundColor: type.color }}>
                        {type.name[0]}
                      </div>
                      <span className="text-sm font-semibold text-slate-700">{type.name}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="relative">
                        <input
                          type="number"
                          min="0"
                          value={leaveAllocations[type._id] || 0}
                          onChange={(e) => setLeaveAllocations({
                            ...leaveAllocations,
                            [type._id]: parseInt(e.target.value) || 0
                          })}
                          className="w-16 pl-3 pr-1 py-1.5 text-center border border-slate-200 bg-white rounded-lg text-sm font-bold text-slate-700 focus:ring-2 focus:ring-gray-200 focus:border-transparent transition-all"
                        />
                      </div>
                      <button
                        onClick={() => handleEditLeaveType(type)}
                        className="p-1.5 text-slate-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-all"
                      >
                        <Edit2 size={14} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* 2. Target Employees */}
            <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden flex flex-col h-[500px]">
              <div className="px-4 py-3 border-b border-slate-100 bg-gradient-to-r from-slate-50/50 to-white">
                <h3 className="font-semibold text-gray-900 flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <Users size={18} className="text-gray-700" />
                    2. Select Recipients
                  </span>
                  <span className={`text-xs font-bold px-2.5 py-1 rounded-lg ${selectedEmployees.length > 0 ? 'bg-gray-100 text-gray-700' : 'bg-slate-100 text-slate-500'}`}>
                    {selectedEmployees.length} selected
                  </span>
                </h3>
              </div>

              <div className="p-4 border-b border-slate-100 bg-slate-50/30">
                <div className="relative">
                  <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
                  <input
                    type="text"
                    placeholder="Search employees..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-9 pr-4 py-2 text-sm bg-white border border-slate-200 rounded-md focus:ring-2 focus:ring-gray-200 focus:border-transparent transition-all"
                  />
                </div>
                <div className="mt-3 flex justify-between items-center px-1">
                  <button onClick={handleSelectAll} className="text-xs font-bold text-gray-700 hover:text-gray-700 hover:underline transition-colors">
                    {selectedEmployees.length === employees.length ? 'Deselect All' : 'Select All'}
                  </button>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-2 space-y-1 scrollbar-thin scrollbar-thumb-slate-200">
                {filteredEmployees.map(emp => {
                  const isSelected = selectedEmployees.includes(emp._id);
                  return (
                    <div
                      key={emp._id}
                      onClick={() => handleEmployeeToggle(emp._id)}
                      className={`flex items-center p-3 rounded-md cursor-pointer transition-all border ${isSelected
                          ? 'bg-gray-100 border-gray-200'
                          : 'hover:bg-slate-50 border-transparent'
                        }`}
                    >
                      <div className={`mr-3 ${isSelected ? 'text-gray-700' : 'text-slate-300'}`}>
                        {isSelected ? <CheckSquare size={20} /> : <Square size={20} />}
                      </div>
                      <div>
                        <div className={`text-sm font-semibold ${isSelected ? 'text-gray-700' : 'text-slate-700'}`}>
                          {emp.name}
                        </div>
                        <div className="text-xs text-slate-400 font-mono">{emp.employeeId}</div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="p-4 border-t border-slate-100 bg-slate-50/50">
                <button
                  onClick={handleBulkAssign}
                  disabled={loading || selectedEmployees.length === 0}
                  className="w-full flex items-center justify-center px-4 py-3 bg-gray-900 hover:bg-gray-800 text-white font-semibold rounded-md shadow-none disabled:opacity-50 disabled:shadow-none disabled:cursor-not-allowed transition-all gap-2"
                >
                  {loading ? (
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <>
                      <Save size={18} />
                      Confirm Assignment
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Right Column: Matrix View */}
          <div className="xl:col-span-2">
            <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden h-full flex flex-col">
              <div className="px-6 py-5 border-b border-slate-200 flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-slate-50/30">
                <div>
                  <h3 className="font-medium text-gray-900 flex items-center gap-2">
                    <Calendar size={18} className="text-gray-700" />
                    Allocation Matrix {year}
                  </h3>
                  <p className="text-xs text-slate-500 font-medium mt-1">Overview of assigned leave balances</p>
                </div>
              </div>

              <div className="overflow-x-auto flex-1 custom-scrollbar">
                <table className="min-w-full divide-y divide-slate-100">
                  <thead className="bg-slate-50">
                    <tr>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500r sticky left-0 bg-slate-50 z-10 border-b border-slate-200 shadow-[4px_0_12px_-4px_rgba(0,0,0,0.05)]">
                        Employee
                      </th>
                      {leaveTypes.map(type => (
                        <th key={type._id} scope="col" className="px-4 py-4 text-center text-xs font-medium text-gray-500r border-b border-slate-200 min-w-[100px]">
                          <div className="flex flex-col items-center gap-1">
                            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: type.color }}></div>
                            {type.name}
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-slate-50">
                    {loadingBalances ? (
                      <tr>
                        <td colSpan={leaveTypes.length + 1} className="px-6 py-20 text-center">
                          <div className="flex flex-col items-center justify-center text-slate-400 gap-3">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-200"></div>
                            <span className="text-sm font-medium">Fetching data...</span>
                          </div>
                        </td>
                      </tr>
                    ) : assignedBalances.length === 0 ? (
                      <tr>
                        <td colSpan={leaveTypes.length + 1} className="px-6 py-20 text-center text-sm text-slate-500">
                          No balances initialized for {year}. <br /> Use the tool on the left to assign initial balances.
                        </td>
                      </tr>
                    ) : (
                      assignedBalances.map((record) => (
                        <tr key={record._id} className="hover:bg-gray-100 transition-colors group">
                          <td className="px-6 py-3 whitespace-nowrap sticky left-0 bg-white group-hover:bg-gray-100 z-10 border-r border-slate-100 shadow-[4px_0_12px_-4px_rgba(0,0,0,0.05)]">
                            <div className="flex items-center">
                              <div className="h-9 w-9 rounded-full bg-slate-100 flex items-center justify-center text-slate-600 font-bold text-xs mr-3 border border-slate-200">
                                {record.employeeId?.name?.charAt(0) || '?'}
                              </div>
                              <div>
                                <div className="text-sm font-medium text-gray-900">{record.employeeId?.name || 'Unknown'}</div>
                                <div className="text-[10px] font-mono text-slate-400">{record.employeeId?.employeeId || ''}</div>
                              </div>
                            </div>
                          </td>
                          {leaveTypes.map(type => {
                            const balance = getBalanceForEmployee(record.employeeId?._id, type._id);
                            const availableDays = balance ? (balance.totalDays - (balance.usedDays || 0) - (balance.pendingDays || 0)) : 0;
                            const isEditing = editingCell?.employeeId === record.employeeId?._id && editingCell?.leaveTypeId === type._id;

                            return (
                              <td key={type._id} className="px-4 py-3 whitespace-nowrap text-center text-sm">
                                {isEditing ? (
                                  <input
                                    autoFocus
                                    type="number"
                                    className="w-16 bg-white border-2 border-gray-200 rounded-lg text-center py-1 text-gray-700 font-bold focus:outline-none shadow-lg"
                                    value={editValue}
                                    onChange={(e) => setEditValue(e.target.value)}
                                    onBlur={() => handleCellSave(record.employeeId?._id, record._id)}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') handleCellSave(record.employeeId?._id, record._id);
                                      if (e.key === 'Escape') setEditingCell(null);
                                    }}
                                  />
                                ) : (
                                  <div
                                    onClick={() => balance && handleCellClick(record.employeeId?._id, type._id, balance.totalDays)}
                                    className={`cursor-pointer inline-flex items-center justify-center w-12 py-1.5 rounded-lg transition-all ${!balance
                                        ? 'text-slate-200 cursor-default'
                                        : 'font-bold text-slate-700 hover:bg-gray-100 hover:text-gray-700 bg-slate-50'
                                      }`}
                                  >
                                    {balance ? availableDays : '—'}
                                  </div>
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Leave Type Modal */}
      <AnimatePresence>
        {showLeaveTypeModal && (
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="bg-white rounded-lg shadow-2xl w-full max-w-md overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-4 py-3 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
                <h3 className="text-lg font-medium text-gray-900">
                  {selectedLeaveType ? 'Edit Leave Type' : 'Create Leave Type'}
                </h3>
                <button
                  onClick={() => setShowLeaveTypeModal(false)}
                  className="text-slate-400 hover:text-slate-600 transition-colors p-1 hover:bg-slate-200 rounded-full"
                >
                  <X size={20} />
                </button>
              </div>

              <div className="p-6 space-y-5">
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1.5">Name</label>
                  <input
                    type="text"
                    value={leaveTypeFormData.name || ''}
                    onChange={(e) => setLeaveTypeFormData({ ...leaveTypeFormData, name: e.target.value })}
                    className="w-full border border-slate-200 rounded-md px-4 py-2.5 focus:ring-2 focus:ring-gray-200 focus:border-transparent text-sm font-medium"
                    placeholder="e.g. Annual Leave"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1.5">Description</label>
                  <textarea
                    value={leaveTypeFormData.description || ''}
                    onChange={(e) => setLeaveTypeFormData({ ...leaveTypeFormData, description: e.target.value })}
                    className="w-full border border-slate-200 rounded-md px-4 py-2.5 focus:ring-2 focus:ring-gray-200 focus:border-transparent text-sm font-medium"
                    rows="2"
                    placeholder="Brief description"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1.5">Default Days</label>
                    <input
                      type="number"
                      min="0"
                      value={leaveTypeFormData.defaultDays || 0}
                      onChange={(e) => setLeaveTypeFormData({ ...leaveTypeFormData, defaultDays: parseInt(e.target.value) })}
                      className="w-full border border-slate-200 rounded-md px-4 py-2.5 focus:ring-2 focus:ring-gray-200 focus:border-transparent text-sm font-medium"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1.5">Color</label>
                    <div className="flex items-center gap-2 h-[42px]">
                      <input
                        type="color"
                        value={leaveTypeFormData.color || '#3B82F6'}
                        onChange={(e) => setLeaveTypeFormData({ ...leaveTypeFormData, color: e.target.value })}
                        className="h-full w-12 border-0 rounded-md p-0 cursor-pointer shadow-sm"
                      />
                      <div className="flex-1 h-full rounded-md bg-slate-50 border border-slate-100 flex items-center justify-center text-xs font-mono text-slate-500">
                        {leaveTypeFormData.color}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-100 p-4 rounded-md space-y-3 border border-gray-200">
                  <label className="flex items-center cursor-pointer group justify-between">
                    <span className="text-sm font-semibold text-slate-700 group-hover:text-gray-700 transition-colors">Requires Manager Approval</span>
                    <input
                      type="checkbox"
                      checked={leaveTypeFormData.requiresApproval || false}
                      onChange={(e) => setLeaveTypeFormData({ ...leaveTypeFormData, requiresApproval: e.target.checked })}
                      className="rounded text-gray-700 focus:ring-gray-200 h-5 w-5 border-slate-300"
                    />
                  </label>
                  <label className="flex items-center cursor-pointer group justify-between">
                    <span className="text-sm font-semibold text-slate-700 group-hover:text-gray-700 transition-colors">Requires Attachments</span>
                    <input
                      type="checkbox"
                      checked={leaveTypeFormData.requiresDocument || false}
                      onChange={(e) => setLeaveTypeFormData({ ...leaveTypeFormData, requiresDocument: e.target.checked })}
                      className="rounded text-gray-700 focus:ring-gray-200 h-5 w-5 border-slate-300"
                    />
                  </label>
                </div>
              </div>

              <div className="px-4 py-3 bg-slate-50 border-t border-slate-100 flex justify-end gap-3">
                <button
                  onClick={() => setShowLeaveTypeModal(false)}
                  className="px-5 py-2.5 bg-white border border-slate-200 rounded-md text-slate-600 font-semibold hover:bg-slate-50 transition-colors shadow-sm"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveLeaveType}
                  className="px-5 py-2.5 bg-gray-900 hover:bg-gray-800 text-white font-semibold rounded-md shadow-none transition-colors"
                >
                  {selectedLeaveType ? 'Save Changes' : 'Create Type'}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default YearlyLeaveSettings;